// src/pages/pharmacy/Analytics.js
import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Package, ShoppingCart } from 'lucide-react';
import pharmacyService from '../../services/pharmacyService';

const Analytics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await pharmacyService.getStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
        <p className="text-gray-600 mt-1">Insights into your pharmacy performance</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Products</p>
              <h3 className="text-2xl font-bold mt-2">{stats?.totalProducts || 0}</h3>
            </div>
            <Package className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Total Orders</p>
              <h3 className="text-2xl font-bold mt-2">
                {(stats?.pendingOrders || 0) + (stats?.completedOrders || 0)}
              </h3>
            </div>
            <ShoppingCart className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Revenue</p>
              <h3 className="text-2xl font-bold mt-2">
                {pharmacyService.formatCurrency(stats?.totalRevenue || 0)}
              </h3>
            </div>
            <TrendingUp className="w-8 h-8 text-purple-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Avg Order</p>
              <h3 className="text-2xl font-bold mt-2">
                {stats?.completedOrders > 0
                  ? pharmacyService.formatCurrency((stats?.totalRevenue || 0) / stats.completedOrders)
                  : pharmacyService.formatCurrency(0)}
              </h3>
            </div>
            <BarChart3 className="w-8 h-8 text-orange-600" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Performance Metrics</h2>
        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Order Completion Rate</span>
              <span className="font-semibold">
                {stats?.completedOrders && stats?.pendingOrders
                  ? Math.round((stats.completedOrders / (stats.completedOrders + stats.pendingOrders)) * 100)
                  : 0}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-600 h-2 rounded-full"
                style={{
                  width: `${stats?.completedOrders && stats?.pendingOrders
                    ? (stats.completedOrders / (stats.completedOrders + stats.pendingOrders)) * 100
                    : 0}%`
                }}
              ></div>
            </div>
          </div>

          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Inventory Utilization</span>
              <span className="font-semibold">
                {stats?.productLimit ? Math.round((stats.totalProducts / stats.productLimit) * 100) : 0}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full"
                style={{
                  width: `${stats?.productLimit ? (stats.totalProducts / stats.productLimit) * 100 : 0}%`
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-bold text-blue-900 mb-2">📊 Advanced Analytics Coming Soon</h3>
        <p className="text-blue-800 text-sm">
          Get detailed insights including sales trends, popular products, customer analytics, and more.
        </p>
      </div>
    </div>
  );
};

export default Analytics;